import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from "react-bootstrap";
import { SingleTrail, TrailDifficulty, TrailStatus, TrailType, CarType, parseIntervalToHours, formatHoursToInterval } from "../../Data/Trails";
import {
  generateKML,
  parseKMLAndGetCoordinates,
} from "../../../Utilities/Helpers";
import * as _ from "lodash";
import IntervalInput from "./components/intervalInput";
import "./Trail.css";
import MultiPhotoUpload from "./components/multiPhotoUpload";

import { useTrail } from "../../../components/Trail/hooks/useTrail";
import { trailService } from "../../../components/Trail/services/trailService";
import { locationFilterService } from "../../../components/Trail/services/locationFilterService";
import TrailFormSection from "./components/TrailFormSection";
import { showError, showSuccess, showWarning } from "../../../Utilities/sweetAlert";
import TagMultiSelect from "../../../components/Tag/TagMultiSelect/TagMultiSelect";
import { Tag } from "../../../Resources/Data/Tags";
import { City, Country } from "../../../Resources/Data/Locations";
import { jtaService, JtaRegion } from "../../../components/Jta/services/jtaService";

const Trail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    trail,
    setTrail,
    originalTrail,
    setOriginalTrail,
    loading,
    setLoading,
    isEditing,
    setNewTrailValue,
  } = useTrail(id);
  
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [fileName, setFileName] = useState("");
  const [cities, setCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [jtaRegions, setJtaRegions] = useState<JtaRegion[]>([]);

  useEffect(() => {
    jtaService.getRegions()
      .then((response) => setJtaRegions(response.data.regions))
      .catch(() => setJtaRegions([]));
  }, []);


  useEffect(() => {
    // Fetch countries first
    locationFilterService.getCountries().then((countriesData) => {
      setCountries(Array.isArray(countriesData) ? countriesData : []);
      
      // If editing, set the selected country based on the trail's city
      if (isEditing && trail.cityId) {
        // Find the city to get its country
        trailService.getCities().then((response) => {
          const city = response.data.cities.find((c: any) => c.id === trail.cityId);
          if (city && city.country) {
            setSelectedCountryId(city.country.id);
            // Fetch cities for this country
            locationFilterService.getCitiesByCountry(city.country.id).then((citiesData) => {
              setCities(Array.isArray(citiesData) ? citiesData : []);
            });
          }
        });
      } else {
        // If creating new trail, don't set a default city - let user choose
        setTrail(prevTrail => ({
          ...prevTrail,
          cityId: 0, // No city selected initially
          difficulty: TrailDifficulty.Easy
        }));
        // Don't fetch cities initially - wait for country selection
        setCities([]);
      }
    }).catch((error) => {
      console.error('Error fetching countries:', error);
      setCountries([]);
    });
  }, [isEditing, setTrail]);

 

  // Handle KML file upload
  function handleUpload() {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".kml";
    fileInput.onchange = (e: any) => {
      const file = e.target.files ? e.target.files[0] : null;
      if (file) {
        if (file.name.endsWith(".kml")) {
          setFileName(file.name);
          const reader = new FileReader();
          reader.onload = (event: ProgressEvent<FileReader>) => {
            const content = event.target?.result as string;
            const parsedCoords = parseKMLAndGetCoordinates(content);
            setTrail((prevTrail) => ({
              ...prevTrail,
              trailPath: {
                type: "LineString",
                coordinates: parsedCoords,
              },
            }));
          };
          reader.readAsText(file);
        } else {
            showWarning("Invalid File", "Please upload a file with a .kml extension.");
        }
      }
    };
    fileInput.click();
  }

  const handleCountryChange = async (countryId: number) => {
    // Only reset city if country actually changed
    if (selectedCountryId !== countryId) {
      setSelectedCountryId(countryId);
      // Reset city selection when country changes
      setTrail(prevTrail => ({ ...prevTrail, cityId: 0 }));
      
      if (countryId) {
        try {
          const citiesData = await locationFilterService.getCitiesByCountry(countryId);
          const citiesArray = Array.isArray(citiesData) ? citiesData : [];
          setCities(citiesArray);
        } catch (error) {
          console.error('Error fetching cities for country:', error);
          showError('Error', 'Failed to fetch cities for selected country');
          setCities([]);
        }
      } else {
        setCities([]);
      }
    }
  };

  function handleDownload() {
    if (!trail.trailPath || !trail.trailPath.coordinates.length) {
      showWarning("No Trail Path", "No trail path available to download.");
      return;
    }
    const kmlContent = generateKML(trail.trailPath.coordinates);
    const blob = new Blob([kmlContent], {
      type: "application/vnd.google-earth.kml+xml",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = trail.name ? `${trail.name}.kml` : "trail.kml";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }


  const handlePhotosChange = (files: File[]) => {
    setUploadedPhotos(files);
  };

  const handlePhotoDelete = (photoUrl: string) => {
      const photoName = photoUrl.split("/").pop(); 
  
      if (photoName) {
        trailService
          .deleteTrailPhoto(trail.id.toString(), photoName)
          .then((response) => {
            
            showSuccess("Photo Deleted", "Photo deleted successfully");
        setTrail((prevTrail) => ({
          ...prevTrail,
          photos: prevTrail.photos?.filter((photo) => photo !== photoUrl),
        }));
          })
          .catch((error) => {
            console.error("Error deleting photo:", error);
            showError("Deletion Failed", "Failed to delete photo");
    
          });
      }
  };
  

  const updateTrail = () => {
    const delta = Object.keys(trail!).reduce((acc, key) => {
        if (trail![key] !== originalTrail![key]) {
            acc[key] = trail![key];
        }
        return acc;
    }, {} as Partial<SingleTrail>);

    if (_.isEmpty(delta) && uploadedPhotos.length === 0 ) {
      showWarning("No Changes", "No changes detected.");

      return;
    }
    setLoading(true);
    
    if(delta.trailPath) {
        const trailPathData = {
            trail_path: delta.trailPath.coordinates
        }
        trailService.updateTrailPath(originalTrail!.id.toString(), trailPathData).then((response) => {


            if(response.status === 200) {
                showSuccess('Path Updated', 'Trail path updated successfully! Please wait while we update the remaining data.');
            }
        });
        delete delta.trailPath;
    }
    if(delta.photos ||uploadedPhotos.length>0){
        const formData=new FormData()
        uploadedPhotos.forEach(file => {
            formData.append('photos', file, file.name);
        });
        trailService.updateTrailPhotos(originalTrail!.id.toString(), formData).then((response) => {
          showSuccess('Photos Updated', 'Trail photos updated successfully! Please wait while we update the remaining data.');
          setUploadedPhotos([]); 

          return trailService
          .getTrail(id!)
          .then((response) => {
            setTrail(response.data.trail as SingleTrail);
            setOriginalTrail(response.data.trail as SingleTrail);
          })
          .catch((err) => {
            showError("Loading Error", "Error loading trail data");

            console.error(err);
          });      
        });
        delete delta.photos;
     
    }
    // eslint-disable-next-line no-restricted-globals
    if(_.isEmpty(delta))  return
    if (delta.cityId) {
        delta.cityId = Number(delta.cityId);
    }
    
    // Handle tags - convert to tagIds array for backend
    if (delta.tags) {
        delta.tagIds = delta.tags.map(tag => tag.id);
        delete delta.tags; // Remove tags from delta, backend expects tagIds
    }
    trailService.updateTrail(originalTrail!.id.toString(), delta).then((response) => {
        if(response.status === 200 || response.status === 201) {
            showSuccess('Trail Updated', 'Trail edited successfully!');
            
            // Reload trail data to refresh display
            trailService.getTrail(id!).then((response) => {
                setUploadedPhotos([]); // Clear new photos list
                setTrail(response.data.trail as SingleTrail);
                setOriginalTrail(response.data.trail as SingleTrail);
                setLoading(false);
            }).catch((reloadError) => {
                console.error('Error reloading trail data:', reloadError);
                showWarning('Update Success', 'Trail updated successfully, but failed to reload data. Please refresh the page.');
                setLoading(false);
            });
        } else {
            // Handle unexpected response status
            showWarning('Update Warning', 'Trail update may not have been completed successfully. Please check and try again.');
            setLoading(false);
        }
    }).catch((error) => {
        console.error('Error updating trail:', error);
        const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
        showError("Update Failed", `Failed to update trail: ${errorMessage}`);
        setLoading(false);
    });
  }
    

  const createTrail = () => {
    if (!trail.cityId || trail.cityId === 0) {
      showWarning("Missing Field", "Please select a city");
      return;
    }
    if (!trail.name) {
      showWarning("Missing Field", "Please fill in the name field");
      return;
    }

    if (!trail.difficulty) {
      showWarning("Missing Field", "Please fill in the difficulty field");
      return;
    }
    if (!trail.time || parseIntervalToHours(trail.time) <= 0) {
      showWarning("Missing Field", "Please fill in a valid time duration");
      return;
    }

    if (!trail.description) {
      showWarning("Missing Field", "Please fill in the description field");
      return;
    }
    if (!trail.trailPath) {
      showWarning("Missing Field", "Please fill in the trail path field");
      return;
    }
    if (!uploadedPhotos.length) {
      showWarning("Missing Photos", "Please upload at least one photo");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", trail.name);
    formData.append("difficulty", trail.difficulty);
    // Convert time to interval string format for backend
    const timeString = typeof trail.time === 'object' ? 
      formatHoursToInterval(parseIntervalToHours(trail.time)) : 
      trail.time;
    formData.append("time", timeString);
    formData.append("description", trail.description);

    // Transform coordinates for trail path
    const coords = trail.trailPath.coordinates
      .map((coord) => coord.join(" "))
      .join(", ");
    formData.append("trail_path", `[${coords}]`);
    formData.append("cityId", trail.cityId as unknown as string);
    trail.type && formData.append("type", trail.type);
    trail.carType && formData.append("carType", trail.carType);
    if (trail.isJtaTrail) {
      formData.append("isJtaTrail", "true");
      if (trail.jtaRegionId) formData.append("jtaRegionId", String(trail.jtaRegionId));
    }
    // Add tags if they exist
    if (trail.tags && trail.tags.length > 0) {
      const tagIds = trail.tags.map(tag => tag.id.toString());
      formData.append("tagIds", JSON.stringify(tagIds));
    }
    
    uploadedPhotos.forEach((file) => {
      formData.append("photos", file, file.name);
    });

    trailService
      .createTrail(formData)
      .then(() => {
        showSuccess("Trail Created", "Trail added successfully!");
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error creating trail:", error);
        showError("Creation Failed", "Error creating trail");
        setLoading(false);
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!trail.name) {
      showError("Validation Error", "Trail name is required");
      return;
    }

    if (!trail.description) {
      showError("Validation Error", "Trail description is required");
      return;
    }

    if (!trail.time) {
      showError("Validation Error", "Trail time is required");
      return;
    }

    if (!trail.cityId) {
      showError("Validation Error", "City is required");
      return;
    }

    if (!selectedCountryId) {
      showError("Validation Error", "Country is required");
      return;
    }

    if (!trail.trailPath || !trail.trailPath.coordinates.length) {
      showError("Validation Error", "Trail path is required");
      return;
    }

    if (!uploadedPhotos.length) {
      showWarning("Missing Photos", "Please upload at least one photo");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", trail.name);
    formData.append("difficulty", trail.difficulty);
    // Convert time to interval string format for backend
    const timeString = typeof trail.time === 'object' ? 
      formatHoursToInterval(parseIntervalToHours(trail.time)) : 
      trail.time;
    formData.append("time", timeString);
    formData.append("description", trail.description);

    // Transform coordinates for trail path
    const coords = trail.trailPath.coordinates
      .map((coord) => coord.join(" "))
      .join(", ");
    formData.append("trail_path", `[${coords}]`);
    formData.append("cityId", trail.cityId as unknown as string);
    trail.type && formData.append("type", trail.type);
    trail.carType && formData.append("carType", trail.carType);
    if (trail.isJtaTrail) {
      formData.append("isJtaTrail", "true");
      if (trail.jtaRegionId) formData.append("jtaRegionId", String(trail.jtaRegionId));
    }
    // Add tags if they exist
    if (trail.tags && trail.tags.length > 0) {
      const tagIds = trail.tags.map(tag => tag.id.toString());
      formData.append("tagIds", JSON.stringify(tagIds));
    }
    
    uploadedPhotos.forEach((file) => {
      formData.append("photos", file, file.name);
    });

    trailService
      .createTrail(formData)
      .then(() => {
        showSuccess("Trail Created", "Trail added successfully!");
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error creating trail:", error);
        showError("Creation Failed", "Error creating trail");
        setLoading(false);
      });
  };

  return (
    <Container className="single-element-container">
      <div className="trail-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2>{isEditing ? `Edit Trail: ${trail?.name || 'Loading...'}` : 'Create New Trail'}</h2>
            {isEditing && trail?.id && (
              <p className="text-muted">Trail ID: {trail.id}</p>
            )}
          </div>
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/trails')}
            className="d-flex align-items-center gap-2"
          >
            <i className="fas fa-arrow-left"></i>
            Back to Trails
          </Button>
        </div>
      </div>

      <div>
          <TrailFormSection 
            title="Photos" 
            icon="fas fa-camera"
            className="photo-section"
          >
            <MultiPhotoUpload
              onPhotosChange={handlePhotosChange}
              onPhotoDelete={handlePhotoDelete}
              initialPhotos={trail?.photos || []}
            />
          </TrailFormSection>

        <div className="trail-form-sections">
          {isEditing && (
            <>
              <InputGroup size="sm" className="mb-3">
                <InputGroup.Text
                  id="inputGroup-sizing-sm"
                  className="meta-element-label"
                >
                  ID
                </InputGroup.Text>
                <Form.Control
                  aria-label="Small"
                  aria-describedby="inputGroup-sizing-sm"
                  value={trail?.id}
                  disabled
                />
              </InputGroup>
              <InputGroup size="sm" className="mb-3">
                <InputGroup.Text
                  id="inputGroup-sizing-sm"
                  className="meta-element-label"
                >
                  Created At
                </InputGroup.Text>
                <Form.Control
                  aria-label="Small"
                  aria-describedby="inputGroup-sizing-sm"
                  value={trail?.createdAt as unknown as string}
                  disabled
                />
              </InputGroup>
              <InputGroup size="sm" className="mb-3">
                <InputGroup.Text
                  id="inputGroup-sizing-sm"
                  className="meta-element-label"
                >
                  Rating
                </InputGroup.Text>
                <Form.Control
                  aria-label="Small"
                  aria-describedby="inputGroup-sizing-sm"
                  value={trail?.rating}
                  disabled
                />
              </InputGroup>
            </>
          )}
          { (
            <>
              <InputGroup size="sm" className="mb-3">
                <InputGroup.Text
                  id="inputGroup-sizing-sm"
                  className="meta-element-label"
                >
                  Country
                </InputGroup.Text>
                <Form.Select
                  aria-label="Small"
                  aria-describedby="inputGroup-sizing-sm"
                  onChange={(e) =>
                    handleCountryChange(parseInt(e.target.value))
                  }
                  value={selectedCountryId || ''}
                >
                  <option value="">Select a country</option>
                  {countries?.map((country: any) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
              <InputGroup size="sm" className="mb-3">
                <InputGroup.Text
                  id="inputGroup-sizing-sm"
                  className="meta-element-label"
                >
                  City
                </InputGroup.Text>
                <Form.Select
                  aria-label="Small"
                  aria-describedby="inputGroup-sizing-sm"
                  onChange={(e) => {
                    const cityId = e.target.value ? parseInt(e.target.value) : 0;
                    setNewTrailValue("cityId", cityId);
                  }}
                  value={trail?.cityId || ""}
                  disabled={!selectedCountryId}
                >
                  <option value="">Select a city</option>
                  {Array.isArray(cities) && cities.length > 0 ? cities.map((city: any) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  )) : (
                    <option value="" disabled>No cities available for selected country</option>
                  )}
                </Form.Select>
              </InputGroup>
            </>
          )}

          {/* Jordan Trail Association */}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text id="inputGroup-sizing-sm" className="meta-element-label">
              Jordan Trail Association
            </InputGroup.Text>
            <InputGroup.Text>
              <Form.Check
                type="switch"
                id="jta-toggle"
                label="Include in JTA Explore page"
                checked={!!trail?.isJtaTrail}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNewTrailValue("isJtaTrail", checked);
                  if (!checked) setNewTrailValue("jtaRegionId", null);
                }}
              />
            </InputGroup.Text>
          </InputGroup>
          {trail?.isJtaTrail && (
            <InputGroup size="sm" className="mb-3">
              <InputGroup.Text id="inputGroup-sizing-sm" className="meta-element-label">
                JTA Region
              </InputGroup.Text>
              <Form.Select
                aria-label="JTA Region"
                value={trail?.jtaRegionId ?? ""}
                onChange={(e) =>
                  setNewTrailValue("jtaRegionId", e.target.value ? parseInt(e.target.value) : null)
                }
              >
                <option value="">Select a region</option>
                {jtaRegions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          )}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Trail Path
            </InputGroup.Text>
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              .kml file only
            </InputGroup.Text>
            <Form.Control
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              placeholder="No file chosen"
              value={fileName}
              disabled
            />
            <Button variant="outline-secondary" onClick={handleUpload}>
              Upload
            </Button>
            <Button variant="outline-secondary" onClick={handleDownload}>
              Download
            </Button>
            
          
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Trail Name
            </InputGroup.Text>
            <Form.Control
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              onChange={(e) => setNewTrailValue("name", e.target.value)}
              value={trail?.name || ""}
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Distance
            </InputGroup.Text>
            <Form.Control
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              disabled
              value={trail?.distance ? `${trail.distance} km` : "Not specified"}
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Elevation Gain
            </InputGroup.Text>
            <Form.Control
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              disabled
              value={trail?.elevationGain ? `${trail.elevationGain} m` : "Not specified"}
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Difficulty
            </InputGroup.Text>
            <Form.Select
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              onChange={(e) =>
                setNewTrailValue(
                  "difficulty",
                  e.target.value as TrailDifficulty
                )
              }
              value={trail?.difficulty || ""}
            >
              {Object.values(TrailDifficulty).map((difficulty) => (
                <option key={difficulty} value={difficulty}>
                  {difficulty}
                </option>
              ))}
            </Form.Select>
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <IntervalInput
              value={isEditing ? originalTrail?.time : trail?.time}
              field="time"
              onChange={setNewTrailValue}
            />
          </InputGroup>
          
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Trail Type
            </InputGroup.Text>
            <Form.Select
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              onChange={(e) =>
                setNewTrailValue("type", e.target.value as TrailType)
              }
              value={trail?.type || ""}
            >
              <option value="">Select trail type</option>
              {Object.values(TrailType).map((type) => (
                <option key={type} value={type}>
                  {type.replace('_', ' ')}
                </option>
              ))}
            </Form.Select>
          </InputGroup>
          
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Car Type Required
            </InputGroup.Text>
            <Form.Select
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              onChange={(e) =>
                setNewTrailValue("carType", e.target.value as CarType)
              }
              value={trail?.carType || ""}
            >
              <option value="">Select car type</option>
              {Object.values(CarType).map((carType) => (
                <option key={carType} value={carType}>
                  {carType === '_4x4' ? '4x4' : carType}
                </option>
              ))}
            </Form.Select>
          </InputGroup>

          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text
              id="inputGroup-sizing-sm"
              className="meta-element-label"
            >
              Description
            </InputGroup.Text>
            <Form.Control
              as="textarea"
              aria-label="Small"
              aria-describedby="inputGroup-sizing-sm"
              onChange={(e) =>
                setNewTrailValue("description", e.target.value)
              }
              value={trail?.description || ""}
            />
          </InputGroup>
          {isEditing && (
            <InputGroup size="sm" className="mb-3">
              <InputGroup.Text
                id="inputGroup-sizing-sm"
                className="meta-element-label"
              >
                Status
              </InputGroup.Text>
              <Form.Select
                aria-label="Small"
                aria-describedby="inputGroup-sizing-sm"
                onChange={(e) =>
                  setNewTrailValue("status", e.target.value as TrailStatus)
                }
                value={trail?.status || ""}
              >
                {Object.values(TrailStatus).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          )}

          {/* Tags Section */}
          <TagMultiSelect
            selectedTags={trail?.tags?.map(tag => ({ id: tag.id, name: tag.name })) || []}
            onTagsChange={(tags: Tag[]) => {
              setNewTrailValue("tags", tags.map(tag => ({ id: tag.id, name: tag.name })));
            }}
            placeholder="Search and select tags for this trail..."
            allowCreate={true}
            maxTags={10}
            className="mb-3"
          />

          <div className="trail-buttons">
            {isEditing && (
              <Button variant="success" onClick={updateTrail} disabled={loading}>
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Update"
                )}
              </Button>
            )}
            {!isEditing && (
              <Button
                variant="success"
                onClick={createTrail}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Create"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Modal show={loading} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Processing your request, please wait...</p>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Trail;
